import React, {
  useEffect,
  useId,
  useState,
  useRef,
  createContext,
  useContext,
} from "react";

const blueprintStyles = `
:root {
  --navy: #0f2942;
  --navy-2: #123a5e;
  --blue: #1976d2;
  --blue-dark: #125aa0;
  --blue-light: #e9f2fd;
  --purple: #7e57c2;
  --teal: #26a69a;
  --bg: #f3f5f8;
  --card: #ffffff;
  --border: #e2e6ec;
  --text: #1b2434;
  --muted: #707c8c;
  --success: #1fae4b;
  --warn: #fb8c00;
  --danger: #e53935;
  --row-alt: #fafbfd;
  --row-hover: #e9f2fd;
  --sidebar-w: 264px;
}
* { box-sizing: border-box; }
html, body, #root { margin: 0; padding: 0; height: 100%; }
body { font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; background: var(--bg); color: var(--text); -webkit-font-smoothing: antialiased; }
button { font-family: inherit; }
@media (prefers-reduced-motion: reduce) {
  *, *::before, *::after { animation-duration: 0.001ms !important; animation-iteration-count: 1 !important; transition-duration: 0.001ms !important; scroll-behavior: auto !important; }
}
.app-blueprint { display: flex; min-height: 100vh; }
.sidebar-blueprint {
  width: var(--sidebar-w); flex-shrink: 0; background: var(--card); color: var(--text); border-right: 1px solid var(--border);
  padding: 20px 14px 24px; position: fixed; top: 0; bottom: 0; left: 0; overflow-y: auto; z-index: 40; transition: transform 0.25s ease;
}
.sidebar-brand { display: flex; align-items: center; gap: 10px; padding: 4px 8px 18px; border-bottom: 1px solid var(--border); margin-bottom: 14px; }
.brand-mark { width: 34px; height: 34px; border-radius: 9px; flex-shrink: 0; background: linear-gradient(135deg, #4fa8e8, #7e57c2); display: flex; align-items: center; justify-content: center; font-weight: 800; font-size: 15px; color: #fff; position: relative; }
.brand-mark::after { content: ''; position: absolute; width: 4px; height: 4px; border-radius: 50%; background: #0f2942; box-shadow: 7px 0 0 #0f2942; left: 11px; top: 19px; }
.brand-text { line-height: 1.1; }
.brand-text b { display: block; font-size: 15px; letter-spacing: 0.3px; color: var(--text); }
.brand-text span { display: block; font-size: 10.5px; color: var(--muted); margin-top: 2px; }
.sidebar-scope { font-size: 10.5px; color: #3d5d80; background: var(--blue-light); border: 1px solid #cfe1f9; border-radius: 7px; padding: 7px 10px; margin: 2px 4px 16px; line-height: 1.4; }
.sidebar-scope b { color: var(--blue-dark); }
.nav-group-label { font-size: 10.5px; font-weight: 700; color: #9aa5b4; letter-spacing: 0.6px; padding: 14px 10px 6px; text-transform: uppercase; }
.nav-item { display: flex; align-items: center; gap: 10px; width: 100%; text-align: left; background: transparent; border: none; border-left: 3px solid transparent; color: #43506a; cursor: pointer; padding: 8px 10px 8px 9px; border-radius: 6px; font-size: 13.2px; margin: 1px 0; }
.nav-item:hover { background: #f1f4f8; }
.nav-item.active { background: var(--blue-light); border-left-color: var(--blue); color: var(--blue-dark); font-weight: 600; }
.nav-dot { width: 6px; height: 6px; border-radius: 50%; background: #b7c0cd; flex-shrink: 0; }
.nav-item.active .nav-dot { background: var(--blue); }
.nav-item .role-name { flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.sidebar-foot { margin-top: 20px; padding: 12px 10px 4px; border-top: 1px solid var(--border); font-size: 10.5px; color: #9aa5b4; line-height: 1.5; }
.main-blueprint { flex: 1; margin-left: var(--sidebar-w); min-width: 0; }
.topbar { position: sticky; top: 0; z-index: 30; background: var(--card); border-bottom: 1px solid var(--border); padding: 14px 26px; display: flex; align-items: center; gap: 16px; }
.menu-btn { display: none; width: 36px; height: 36px; border-radius: 8px; border: 1px solid var(--border); background: var(--card); align-items: center; justify-content: center; cursor: pointer; color: var(--text); }
.topbar-titles { flex: 1; min-width: 0; }
.topbar-titles h1 { margin: 0; font-size: 19px; font-weight: 700; letter-spacing: -0.2px; }
.topbar-titles p { margin: 2px 0 0; font-size: 12.5px; color: var(--muted); }
.topbar-right { display: flex; align-items: center; gap: 14px; flex-shrink: 0; }
.blueprint-pill { font-size: 11px; font-weight: 600; color: #8a6a1a; background: #fff4e0; border: 1px solid #f0dda8; padding: 5px 10px; border-radius: 20px; white-space: nowrap; }
.dn-toggle { display: flex; align-items: center; gap: 7px; font-size: 12px; color: var(--muted); cursor: pointer; user-select: none; }
.switch { width: 34px; height: 19px; border-radius: 20px; background: var(--border); position: relative; transition: 0.15s; }
.switch::after { content: ''; position: absolute; width: 15px; height: 15px; border-radius: 50%; background: #fff; top: 2px; left: 2px; box-shadow: 0 1px 2px rgba(0, 0, 0, 0.3); transition: 0.15s; }
.dn-toggle input { display: none; }
.dn-toggle input:checked + .switch { background: var(--blue); }
.dn-toggle input:checked + .switch::after { left: 17px; }
.avatar { width: 32px; height: 32px; border-radius: 50%; background: var(--navy-2); color: #fff; display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 700; flex-shrink: 0; }
.dash-wrap { padding: 22px 26px 46px; }
.dash-intro { margin: 0 0 18px; }
.dash-intro h2 { margin: 0 0 4px; font-size: 15px; font-weight: 700; }
.dash-intro p { margin: 0; font-size: 12.5px; color: var(--muted); max-width: 760px; line-height: 1.5; }
.grid { display: grid; grid-template-columns: repeat(12, 1fr); gap: 18px; }
.kpi-row { grid-column: 1 / -1; display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 16px; margin-bottom: 2px; }
.card { background: var(--card); border: 1px solid var(--border); border-radius: 12px; padding: 18px 20px; position: relative; overflow: hidden; }
.kpi .kpi-label { font-size: 11.5px; color: var(--muted); font-weight: 600; margin: 0 0 8px; }
.kpi .kpi-value { font-size: 26px; font-weight: 800; letter-spacing: -0.5px; line-height: 1; }
.kpi .kpi-sub { font-size: 11px; margin-top: 8px; display: flex; align-items: center; gap: 5px; }
.kpi .kpi-sub.up { color: var(--success); }
.kpi .kpi-sub.down { color: var(--danger); }
.kpi .kpi-sub.flat { color: var(--muted); }
.kpi-spark { position: absolute; right: 14px; bottom: 14px; width: 76px; height: 26px; opacity: 0.9; }
.card-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 10px; margin-bottom: 14px; }
.card-head h3 { margin: 0; font-size: 13.5px; font-weight: 700; }
.card-head p { margin: 3px 0 0; font-size: 11px; color: var(--muted); }
.head-badge { font-size: 10px; font-weight: 700; color: #fff; background: var(--danger); border-radius: 20px; min-width: 18px; height: 18px; padding: 0 5px; display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0; }
.devnote { display: none; font-size: 10px; color: #8a6a1a; background: #fff7e8; border: 1px dashed #e7c67a; padding: 3px 8px; border-radius: 6px; white-space: nowrap; flex-shrink: 0; }
body.devnotes-on .devnote { display: inline-block; }
.chart-svg { width: 100%; height: auto; display: block; overflow: visible; }
.bar-label { font-size: 9px; fill: var(--muted); font-family: inherit; }
.bar-value { font-size: 9.5px; fill: var(--text); font-weight: 700; font-family: inherit; }
.axis-label { font-size: 8.5px; fill: #a6afbb; font-family: inherit; }
.end-label { font-size: 13px; font-weight: 800; font-family: inherit; }
.lead-label { font-size: 11.5px; font-weight: 600; fill: var(--muted); font-family: inherit; }
.table-scroll { overflow-x: auto; -webkit-overflow-scrolling: touch; margin: 0 -2px; }
table { width: 100%; border-collapse: collapse; font-size: 12px; border-radius: 6px; overflow: hidden; }
thead th { text-align: left; font-size: 10.5px; text-transform: uppercase; letter-spacing: 0.3px; color: #fff; font-weight: 700; padding: 9px 10px; background: var(--blue); }
thead th:first-child { border-top-left-radius: 6px; }
thead th:last-child { border-top-right-radius: 6px; }
tbody td { padding: 9px 10px; border-bottom: 1px solid var(--border); }
tbody tr:nth-child(even) td { background: var(--row-alt); }
tbody tr:last-child td { border-bottom: none; }
tbody tr { transition: background 0.15s; }
tbody tr.lb-row { cursor: pointer; }
tbody tr.lb-row:hover td { background: var(--row-hover) !important; }
tbody tr.lb-row.lb-active td { background: var(--row-hover) !important; }
.tag { font-size: 10.5px; font-weight: 600; padding: 3px 9px; border-radius: 20px; display: inline-block; }
.tag.green { background: #e4f5ec; color: var(--success); }
.tag.amber { background: #fcf0dc; color: var(--warn); }
.tag.red { background: #fbe7e4; color: var(--danger); }
.tag.blue { background: #e8eefc; color: var(--blue); }
.cell-warn { color: var(--warn); font-weight: 700; }
.cell-danger { color: var(--danger); font-weight: 700; }
.cell-good { color: var(--success); font-weight: 700; }
.medal { display: inline-flex; align-items: center; justify-content: center; width: 20px; height: 20px; border-radius: 50%; font-size: 11px; font-weight: 800; color: #fff; }
.medal.m1 { background: #e3a008; }
.medal.m2 { background: #9aa5b4; }
.medal.m3 { background: #b5651d; }
.rank-num { color: var(--muted); font-weight: 600; font-size: 11.5px; }
.sort-note { font-size: 10.5px; color: var(--muted); text-align: right; margin-top: 8px; }
.focus-hint { font-size: 10.5px; color: var(--muted); text-align: left; margin-top: 8px; }
.list-item { display: flex; align-items: center; gap: 12px; padding: 9px 0; border-bottom: 1px solid var(--border); }
.list-item:last-child { border-bottom: none; }
.list-item .li-icon { width: 32px; height: 32px; border-radius: 8px; background: #eef2fb; color: var(--blue); display: flex; align-items: center; justify-content: center; flex-shrink: 0; font-size: 14px; font-weight: 700; }
.list-item .li-body { flex: 1; min-width: 0; }
.list-item .li-title { font-size: 12.5px; font-weight: 600; }
.list-item .li-sub { font-size: 11px; color: var(--muted); margin-top: 1px; }
.list-item .li-meta { font-size: 11px; color: var(--muted); flex-shrink: 0; text-align: right; }
.ring-cluster { display: flex; gap: 28px; flex-wrap: wrap; }
.ring-item { text-align: center; }
.ring-item .ring-cap { font-size: 10px; color: var(--muted); font-weight: 700; letter-spacing: 0.3px; margin-top: 8px; }
.ring-item .ring-name { font-size: 12px; font-weight: 600; margin-top: 2px; }
.ring-item .ring-sub { font-size: 10.5px; color: var(--muted); margin-top: 1px; }
.chip-row { display: flex; flex-wrap: wrap; gap: 8px; }
.chip { font-size: 11.5px; background: #eef2fb; color: var(--navy); padding: 6px 12px; border-radius: 20px; font-weight: 600; }
.tabs { display: flex; gap: 6px; margin-bottom: 14px; flex-wrap: wrap; }
.tab-btn { border: 1px solid var(--border); background: var(--card); color: var(--muted); font-size: 12px; font-weight: 600; padding: 7px 13px; border-radius: 7px; cursor: pointer; }
.tab-btn.active { background: var(--navy); color: #fff; border-color: var(--navy); }
.alerts-strip { display: flex; gap: 10px; overflow-x: auto; padding-bottom: 2px; margin: 0 -2px; }
.alert-chip { flex: 0 0 auto; min-width: 230px; max-width: 260px; border-radius: 9px; padding: 10px 12px; display: flex; gap: 9px; align-items: flex-start; border: 1px solid transparent; }
.alert-chip.amber { background: #fff8ec; border-color: #f6e2b8; }
.alert-chip.red { background: #fdeeed; border-color: #f5cfcb; }
.alert-chip.blue { background: #ebf3fe; border-color: #cfe1f9; }
.alert-ic { font-size: 14px; flex-shrink: 0; margin-top: 1px; }
.alert-title { font-size: 11.5px; font-weight: 700; color: #1b2434; }
.alert-desc { font-size: 11px; color: #5b6b7c; margin-top: 1px; line-height: 1.35; }
.source-note { grid-column: 1 / -1; display: flex; justify-content: space-between; gap: 12px; flex-wrap: wrap; font-size: 10.5px; color: #a6afbb; padding-top: 6px; border-top: 1px solid var(--border); margin-top: 4px; }
.s3 { grid-column: span 3; }
.s4 { grid-column: span 4; }
.s5 { grid-column: span 5; }
.s6 { grid-column: span 6; }
.s7 { grid-column: span 7; }
.s8 { grid-column: span 8; }
.s9 { grid-column: span 9; }
.s12 { grid-column: span 12; }
@media (max-width: 1180px) { .s3, .s4 { grid-column: span 6; } .s5, .s7, .s8, .s9 { grid-column: span 12; } }
@media (max-width: 640px) { .s3, .s4, .s5, .s6, .s7, .s8, .s9 { grid-column: span 12; } .dash-wrap { padding: 16px 14px 50px; } .topbar { padding: 12px 14px; } }
.sidebar-scrim { display: none; position: fixed; inset: 0; background: rgba(6, 12, 24, 0.55); z-index: 35; }
@media (max-width: 960px) { .menu-btn { display: flex; } .sidebar-blueprint { transform: translateX(-100%); } .main-blueprint { margin-left: 0; } }
@media (max-width: 640px) { .topbar-titles h1 { font-size: 15px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; } .topbar-titles p { display: none; } .dn-toggle .dn-label { display: none; } .blueprint-pill { display: none; } }
::-webkit-scrollbar { width: 9px; height: 9px; }
::-webkit-scrollbar-thumb { background: #c7cfda; border-radius: 20px; }
@keyframes fadeSlideIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
.dash-enter { animation: fadeSlideIn 0.38s cubic-bezier(0.22, 0.7, 0.32, 1) both; }
@keyframes fadeSlideUp { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
.anim-card { animation: fadeSlideUp 0.42s cubic-bezier(0.22, 0.7, 0.32, 1) both; }
@keyframes growBar { from { transform: scaleY(0); } to { transform: scaleY(1); } }
.bar-rect { transform-box: fill-box; transform-origin: bottom; animation: growBar 0.62s cubic-bezier(0.22, 0.7, 0.32, 1) both; }
@keyframes drawStroke { from { stroke-dashoffset: var(--len); } to { stroke-dashoffset: 0; } }
.draw-path { stroke-dasharray: var(--len); animation: drawStroke 0.9s cubic-bezier(0.22, 0.7, 0.32, 1) both; }
@keyframes ringFill { from { stroke-dashoffset: var(--circ); } to { stroke-dashoffset: var(--off); } }
.ring-fg { animation: ringFill 1s cubic-bezier(0.22, 0.7, 0.32, 1) both; animation-delay: 0.1s; }
@keyframes popIn { from { opacity: 0; transform: scale(0.85); } to { opacity: 1; transform: scale(1); } }
.dot-pop { animation: popIn 0.4s cubic-bezier(0.22, 0.7, 0.32, 1) both; }
.alert-anim { animation: fadeSlideUp 0.4s cubic-bezier(0.22, 0.7, 0.32, 1) both; }
.multiline-series { transition: opacity 0.25s; }
.multiline-series.dimmed { opacity: 0.18; }
#chart-tip { position: fixed; z-index: 100; pointer-events: none; background: #12202f; color: #fff; font-size: 11px; padding: 6px 9px; border-radius: 6px; box-shadow: 0 6px 18px rgba(0, 0, 0, 0.25); opacity: 0; transform: translate(-50%, -120%); transition: opacity 0.1s; white-space: nowrap; }
#chart-tip.show { opacity: 1; }
#chart-tip b { font-weight: 700; }
`;

function dist(a, b) {
  return Math.hypot(b[0] - a[0], b[1] - a[1]);
}

function polylineLength(pts) {
  let d = 0;
  for (let i = 1; i < pts.length; i++) d += dist(pts[i - 1], pts[i]);
  return Math.max(20, d);
}

function niceMax(v) {
  if (v <= 0) return 10;
  const mag = Math.pow(10, Math.floor(Math.log10(v)));
  const n = v / mag;
  const steps = [1, 1.2, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10];
  for (const s of steps) {
    if (n <= s) return s * mag;
  }
  return 10 * mag;
}

function gridLines(max, W, H, padL, padR, padT, padB, ticks = 4) {
  const lines = [];
  const labels = [];
  for (let i = 0; i <= ticks; i++) {
    const val = (max / ticks) * i;
    const y = H - padB - (val / max) * (H - padT - padB);
    lines.push({ x1: padL, y1: y, x2: W - padR, y2: y });
    labels.push({ x: padL - 8, y: y + 3, text: Math.round(val) });
  }
  return { lines, labels };
}

function smoothPath(pts) {
  if (pts.length < 3) {
    return "M" + pts.map((p) => `${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(" L");
  }
  let d = `M${pts[0][0].toFixed(1)} ${pts[0][1].toFixed(1)}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i === 0 ? 0 : i - 1];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[Math.min(i + 2, pts.length - 1)];
    const c1x = p1[0] + (p2[0] - p0[0]) / 6;
    const c1y = p1[1] + (p2[1] - p0[1]) / 6;
    const c2x = p2[0] - (p3[0] - p1[0]) / 6;
    const c2y = p2[1] - (p3[1] - p1[1]) / 6;
    d += ` C${c1x.toFixed(1)} ${c1y.toFixed(1)} ${c2x.toFixed(1)} ${c2y.toFixed(1)} ${p2[0].toFixed(1)} ${p2[1].toFixed(1)}`;
  }
  return d;
}

function useReducedMotion() {
  const [reduced, setReduced] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = () => setReduced(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  return reduced;
}

function useCountUp(value, { duration = 900, decimals = 0 } = {}) {
  const reduced = useReducedMotion();
  const [display, setDisplay] = useState(reduced ? value : 0);
  const raf = useRef(null);

  useEffect(() => {
    if (value === undefined || value === null) return;
    if (reduced) {
      setDisplay(value);
      return;
    }
    const t0 = performance.now();
    function tick(t) {
      const p = Math.min(1, (t - t0) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(value * eased);
      if (p < 1) raf.current = requestAnimationFrame(tick);
    }
    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
  }, [value, reduced, duration]);

  return display.toFixed(decimals).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

const TipCtx = createContext(null);

function TooltipProvider({ children }) {
  const [tip, setTip] = useState(null);
  const api = useRef({
    show(label, value, evt) {
      setTip({ x: evt.clientX, y: evt.clientY, label, value });
    },
    move(evt) {
      setTip((t) => (t ? { ...t, x: evt.clientX, y: evt.clientY } : t));
    },
    hide() {
      setTip(null);
    },
  }).current;

  return (
    <TipCtx.Provider value={api}>
      {children}
      <div id="chart-tip" className={tip ? "show" : ""} style={tip ? { left: tip.x, top: tip.y } : undefined}>
        {tip ? (
          <>
            {tip.label}: <b>{tip.value}</b>
          </>
        ) : null}
      </div>
    </TipCtx.Provider>
  );
}

function useTipHandlers(label, value) {
  const tip = useContext(TipCtx);
  return {
    onMouseEnter: (e) => tip.show(label, value, e),
    onMouseMove: (e) => tip.move(e),
    onMouseLeave: () => tip.hide(),
  };
}

function Card({ span = "s12", title, note, sub, badge, children }) {
  return (
    <div className={`card anim-card ${span}`}>
      <div className="card-head">
        <div>
          <h3>{title}</h3>
          {sub && <p>{sub}</p>}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {badge !== undefined && <span className="head-badge">{badge}</span>}
          {note && <span className="devnote">{note}</span>}
        </div>
      </div>
      {children}
    </div>
  );
}

function Sparkline({ values, color }) {
  const reduced = useReducedMotion();
  const W = 140,
    H = 44,
    pad = 4;
  const max = Math.max(...values),
    min = Math.min(...values);
  const span = max - min || 1;
  const stepX = (W - pad * 2) / (values.length - 1);
  const pts = values.map((v, i) => [pad + i * stepX, H - pad - ((v - min) / span) * (H - pad * 2)]);
  const path = smoothPath(pts);
  const len = polylineLength(pts);
  const last = pts[pts.length - 1];
  const uid = useId();

  return (
    <svg className="kpi-spark" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none">
      <path
        d={path}
        fill="none"
        stroke={color}
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={reduced ? "" : "draw-path"}
        style={reduced ? undefined : { "--len": len.toFixed(0) }}
        key={uid + JSON.stringify(values)}
      />
      <circle cx={last[0]} cy={last[1]} r="2.6" fill={color} />
    </svg>
  );
}

function Ring({ percent, color = "#1976D2", size = 96, stroke = 12, centerLabel, showLabel = true }) {
  const reduced = useReducedMotion();
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const off = c * (1 - percent / 100);
  const label = centerLabel !== undefined ? centerLabel : `${percent}%`;

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#E2E6EC" strokeWidth={stroke} />
      <circle
        className={reduced ? "" : "ring-fg"}
        style={reduced ? undefined : { "--circ": c.toFixed(1), "--off": off.toFixed(1) }}
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke={color}
        strokeWidth={stroke}
        strokeDasharray={c.toFixed(1)}
        strokeDashoffset={reduced ? off.toFixed(1) : c.toFixed(1)}
        strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
      {showLabel && (
        <text
          x="50%"
          y="50%"
          textAnchor="middle"
          dominantBaseline="central"
          style={{ fontSize: size * 0.19, fontWeight: 800, fill: "var(--text)" }}
        >
          {label}
        </text>
      )}
    </svg>
  );
}

function RingCluster({ items }) {
  return (
    <div className="ring-cluster">
      {items.map((it, i) => (
        <div className="ring-item" key={i}>
          <Ring percent={it.percent} color={it.color} size={88} stroke={10} />
          <div className="ring-cap">{(it.tag || "").toUpperCase()}</div>
          <div className="ring-name">{it.label}</div>
          {it.sub && <div className="ring-sub">{it.sub}</div>}
        </div>
      ))}
    </div>
  );
}

function BarChart({ labels, values, color = "#1976D2", max: maxOverride, fmt, dim }) {
  const W = 540,
    H = 210,
    padB = 30,
    padT = 14,
    padL = 34,
    padR = 8;
  const max = maxOverride || niceMax(Math.max(...values) * 1.25);
  const n = values.length;
  const gap = Math.min(22, ((W - padL - padR) / n) * 0.35);
  const barW = (W - padL - padR - gap * (n - 1)) / n;
  const grid = gridLines(max, W, H, padL, padR, padT, padB, 4);
  const gid = "barg" + useId().replace(/[:]/g, "");

  return (
    <svg className="chart-svg" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet">
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} />
          <stop offset="100%" stopColor={color} stopOpacity="0.72" />
        </linearGradient>
      </defs>
      {grid.lines.map((l, i) => (
        <line key={i} x1={l.x1} y1={l.y1} x2={l.x2} y2={l.y2} stroke="#EDF0F4" strokeWidth="1" />
      ))}
      {grid.labels.map((l, i) => (
        <text key={i} x={l.x} y={l.y} textAnchor="end" className="axis-label">
          {l.text}
        </text>
      ))}
      <line x1={padL} y1={H - padB} x2={W - padR} y2={H - padB} stroke="#DDE2E9" strokeWidth="1" />
      {values.map((v, i) => {
        const h = Math.max(2, (v / max) * (H - padT - padB));
        const x = padL + i * (barW + gap);
        const y = H - padB - h;
        return (
          <BarInner
            key={i}
            x={x}
            y={y}
            width={barW}
            height={h}
            gid={gid}
            index={i}
            label={labels[i]}
            value={v}
            dim={dim && dim[i]}
            fmt={fmt}
            bottomY={H - 10}
          />
        );
      })}
    </svg>
  );
}

function BarInner({ x, y, width, height, gid, index, label, value, dim, fmt, bottomY }) {
  const reduced = useReducedMotion();
  const tip = useTipHandlers(label, fmt ? fmt(value) : value);

  return (
    <>
      <rect
        className={reduced ? "" : "bar-rect"}
        style={reduced ? undefined : { animationDelay: `${index * 55}ms` }}
        x={x}
        y={y}
        width={width}
        height={height}
        rx="5"
        fill={`url(#${gid})`}
        opacity={dim ? 0.35 : 1}
        {...tip}
      />
      <text className="bar-value" x={x + width / 2} y={y - 7} textAnchor="middle">
        {fmt ? fmt(value) : value}
      </text>
      <text className="bar-label" x={x + width / 2} y={bottomY} textAnchor="middle">
        {label}
      </text>
    </>
  );
}

function Dot({ cx, cy, color, index, label, value }) {
  const reduced = useReducedMotion();
  const tip = useTipHandlers(label, value);
  return (
    <circle
      cx={cx}
      cy={cy}
      r="3.4"
      fill="#fff"
      stroke={color}
      strokeWidth="2.2"
      className={reduced ? "" : "dot-pop"}
      style={reduced ? undefined : { animationDelay: `${500 + index * 40}ms` }}
      {...tip}
    />
  );
}

function LineChart({ labels, values, color = "#1976D2", max: maxOverride, min: minOverride }) {
  const reduced = useReducedMotion();
  const W = 540,
    H = 210,
    padB = 30,
    padT = 18,
    padL = 34,
    padR = 16;
  const max = maxOverride || niceMax(Math.max(...values) * 1.15);
  const min = minOverride !== undefined ? minOverride : 0;
  const n = values.length;
  const stepX = (W - padL - padR) / (n - 1);
  const scaleY = (v) => H - padB - ((v - min) / (max - min)) * (H - padT - padB);
  const pts = values.map((v, i) => [padL + i * stepX, scaleY(v)]);
  const path = smoothPath(pts);
  const len = polylineLength(pts);
  const area = path + ` L${pts[pts.length - 1][0].toFixed(1)} ${H - padB} L${pts[0][0].toFixed(1)} ${H - padB} Z`;
  const grid = gridLines(max, W, H, padL, padR, padT, padB, 4);
  const gid = "lineg" + useId().replace(/[:]/g, "");

  return (
    <svg className="chart-svg" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet">
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.28" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      {grid.lines.map((l, i) => (
        <line key={i} x1={l.x1} y1={l.y1} x2={l.x2} y2={l.y2} stroke="#EDF0F4" strokeWidth="1" />
      ))}
      {grid.labels.map((l, i) => (
        <text key={i} x={l.x} y={l.y} textAnchor="end" className="axis-label">
          {l.text}
        </text>
      ))}
      <path d={area} fill={`url(#${gid})`} />
      <path
        d={path}
        fill="none"
        stroke={color}
        strokeWidth="2.6"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={reduced ? "" : "draw-path"}
        style={reduced ? undefined : { "--len": len.toFixed(0) }}
      />
      {pts.map((p, i) => (
        <Dot key={i} cx={p[0]} cy={p[1]} color={color} index={i} label={labels[i]} value={values[i]} />
      ))}
      {labels.map((l, i) => (
        <text key={i} className="bar-label" x={pts[i][0]} y={H - 10} textAnchor="middle">
          {l}
        </text>
      ))}
    </svg>
  );
}

function ComboBar({ x, y, width, height, gid, index, label, value }) {
  const reduced = useReducedMotion();
  const tip = useTipHandlers(label, value);
  return (
    <rect
      className={reduced ? "" : "bar-rect"}
      style={reduced ? undefined : { animationDelay: `${index * 55}ms` }}
      x={x}
      y={y}
      width={width}
      height={height}
      rx="5"
      fill={`url(#${gid})`}
      {...tip}
    />
  );
}

function ComboDot({ cx, cy, color, index, label, value }) {
  const reduced = useReducedMotion();
  const tip = useTipHandlers(label, value);
  return (
    <circle
      cx={cx}
      cy={cy}
      r="3.2"
      fill={color}
      className={reduced ? "" : "dot-pop"}
      style={reduced ? undefined : { animationDelay: `${450 + index * 45}ms` }}
      {...tip}
    />
  );
}

function ComboChart({ labels, bars, line, barColor = "#26A69A", lineColor = "#2B3A4A", max: maxOverride }) {
  const reduced = useReducedMotion();
  const W = 540,
    H = 220,
    padB = 30,
    padT = 14,
    padL = 36,
    padR = 10;
  const max = maxOverride || niceMax(Math.max(Math.max(...bars), Math.max(...line)) * 1.2);
  const n = bars.length;
  const gap = Math.min(22, ((W - padL - padR) / n) * 0.35);
  const barW = (W - padL - padR - gap * (n - 1)) / n;
  const grid = gridLines(max, W, H, padL, padR, padT, padB, 4);
  const scaleY = (v) => H - padB - (v / max) * (H - padT - padB);
  const gid = "combog" + useId().replace(/[:]/g, "");

  const linePts = bars.map((_, i) => [padL + i * (barW + gap) + barW / 2, scaleY(line[i])]);
  const path = smoothPath(linePts);
  const len = polylineLength(linePts);

  return (
    <svg className="chart-svg" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet">
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={barColor} />
          <stop offset="100%" stopColor={barColor} stopOpacity="0.55" />
        </linearGradient>
      </defs>
      {grid.lines.map((l, i) => (
        <line key={i} x1={l.x1} y1={l.y1} x2={l.x2} y2={l.y2} stroke="#EDF0F4" strokeWidth="1" />
      ))}
      {grid.labels.map((l, i) => (
        <text key={i} x={l.x} y={l.y} textAnchor="end" className="axis-label">
          {l.text}
        </text>
      ))}
      <line x1={padL} y1={H - padB} x2={W - padR} y2={H - padB} stroke="#DDE2E9" strokeWidth="1" />
      {bars.map((v, i) => {
        const h = Math.max(2, (v / max) * (H - padT - padB));
        const x = padL + i * (barW + gap);
        const y = H - padB - h;
        return <ComboBar key={i} x={x} y={y} width={barW} height={h} gid={gid} index={i} label={labels[i]} value={v} />;
      })}
      {labels.map((l, i) => (
        <text key={i} className="bar-label" x={padL + i * (barW + gap) + barW / 2} y={H - 10} textAnchor="middle">
          {l}
        </text>
      ))}
      <path d={path} fill="none" stroke={lineColor} strokeWidth="2" strokeLinecap="round" className={reduced ? "" : "draw-path"} style={reduced ? undefined : { "--len": len.toFixed(0), animationDelay: ".35s" }} />
      {linePts.map((p, i) => (
        <ComboDot key={i} cx={p[0]} cy={p[1]} color={lineColor} index={i} label={`${labels[i]} target`} value={line[i]} />
      ))}
    </svg>
  );
}

function EndDot({ cx, cy, color, label, value }) {
  const tip = useTipHandlers(label, value);
  return <circle cx={cx} cy={cy} r="5.5" fill={color} {...tip} />;
}

function StartDot({ cx, cy, color, label, value }) {
  const tip = useTipHandlers(label, value);
  return <circle cx={cx} cy={cy} r="5" fill={color} {...tip} />;
}

function MultiLineChart({ series, xLabels = [], max: maxOverride, min: minOverride, activeSeries }) {
  const reduced = useReducedMotion();
  const n = series[0].values.length;
  const rows = series.length;
  const W = 560,
    H = Math.max(200, 64 * rows + 50),
    padL = 96,
    padR = 96,
    padT = 26,
    padB = 26;
  const allVals = series.flatMap((s) => s.values);
  const rawMin = Math.min(...allVals),
    rawMax = Math.max(...allVals);
  const span = rawMax - rawMin || 10;
  const min = minOverride !== undefined ? minOverride : rawMin - span * 0.18;
  const max = maxOverride !== undefined ? maxOverride : rawMax + span * 0.18;
  const stepX = (W - padL - padR) / (n - 1);
  const scaleY = (v) => padT + (1 - (v - min) / (max - min)) * (H - padT - padB);

  return (
    <svg className="chart-svg" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet">
      {Array.from({ length: n }).map((_, i) => {
        const x = padL + i * stepX;
        return <line key={i} x1={x} y1={padT - 8} x2={x} y2={H - padB + 8} stroke="#EEF1F5" strokeWidth="1" />;
      })}
      {xLabels.map((l, i) => (
        <text key={i} x={padL + i * stepX} y={padT - 12} textAnchor="middle" style={{ fontWeight: 700, fontSize: 11, fill: "#8A93A3" }}>
          {l}
        </text>
      ))}
      {series.map((s, ri) => {
        const startVal = s.values[0];
        const endVal = s.values[s.values.length - 1];
        const pts = s.values.map((v, i) => [padL + i * stepX, scaleY(v)]);
        const path = pts.map((p, i) => (i === 0 ? `M${p[0].toFixed(1)} ${p[1].toFixed(1)}` : `L${p[0].toFixed(1)} ${p[1].toFixed(1)}`)).join(" ");
        const up = endVal >= startVal;
        const dimmed = activeSeries && activeSeries !== s.id;
        return (
          <g
            key={s.id}
            className={`multiline-series${dimmed ? " dimmed" : ""}`}
            style={
              reduced
                ? undefined
                : { animation: "fadeSlideUp .5s cubic-bezier(.22,.7,.32,1) both", animationDelay: `${ri * 90}ms` }
            }
          >
            <path d={path} fill="none" stroke={s.color} strokeWidth="3" strokeDasharray={s.dashed ? "7 6" : undefined} strokeLinecap="round" />
            <StartDot cx={pts[0][0]} cy={pts[0][1]} color={s.color} label={`${s.name} (start)`} value={startVal} />
            <text className="lead-label" x={pts[0][0] - 14} y={pts[0][1] + 4} textAnchor="end">
              {s.name} · {startVal}
            </text>
            <EndDot cx={pts[pts.length - 1][0]} cy={pts[pts.length - 1][1]} color={s.color} label={`${s.name} (latest)`} value={endVal} />
            <text className="end-label" fill={s.color} x={pts[pts.length - 1][0] + 14} y={pts[pts.length - 1][1] + 5}>
              {endVal} {up ? "▲" : "▼"}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

function trendGlyph(t) {
  if (t === "up") return "▲";
  if (t === "down") return "▼";
  return "•";
}

function KpiCard({ item, index }) {
  const hasRaw = item.raw !== undefined;
  const animated = useCountUp(hasRaw ? item.raw : 0, { decimals: item.decimals || 0 });
  const value = hasRaw ? `${item.prefix || ""}${animated}${item.suffix || ""}` : item.value;
  const sparkColor = item.sparkColor || (item.trend === "down" ? "#E53935" : "#1976D2");

  return (
    <div className="card kpi anim-card" style={{ animationDelay: `${index * 45}ms` }}>
      <p className="kpi-label">{item.label}</p>
      <div className="kpi-value">{value}</div>
      {item.sub && <div className={`kpi-sub ${item.trend || "flat"}`}>{trendGlyph(item.trend)} {item.sub}</div>}
      {item.spark && <Sparkline values={item.spark} color={sparkColor} />}
    </div>
  );
}

function KpiRow({ items }) {
  return (
    <div className="kpi-row">
      {items.map((item, i) => (
        <KpiCard key={i} item={item} index={i} />
      ))}
    </div>
  );
}

function renderCell(cell) {
  if (cell && typeof cell === "object") {
    if (cell.tone === "tag") return <span className={`tag ${cell.cls}`}>{cell.text}</span>;
    return <span className={cell.cls || ""}>{cell.text}</span>;
  }
  return cell;
}

function medalFor(i) {
  if (i === 0) return <span className="medal m1">1</span>;
  if (i === 1) return <span className="medal m2">2</span>;
  if (i === 2) return <span className="medal m3">3</span>;
  return <span className="rank-num">{i + 1}</span>;
}

function Leaderboard({ span, title, note, sub, badge, cols, rows, sortNote, focusChart, activeRowId, onFocusRow }) {
  const minW = Math.max(260, (cols.length + 1) * 92);

  return (
    <Card span={span} title={title} note={note} sub={sub} badge={badge}>
      <div className="table-scroll">
        <table style={{ minWidth: minW }}>
          <thead>
            <tr>
              <th>#</th>
              {cols.map((c, i) => (
                <th key={i}>{c}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr
                key={i}
                className={`lb-row${focusChart && activeRowId === r.id ? " lb-active" : ""}`}
                onClick={focusChart ? () => onFocusRow(r.id) : undefined}
              >
                <td>{medalFor(i)}</td>
                {r.cells.map((cell, ci) => (
                  <td key={ci}>{renderCell(cell)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {sortNote && <div className="sort-note">{sortNote}</div>}
      {focusChart && <div className="focus-hint">Tap a row to focus the chart on that item — tap again to reset.</div>}
    </Card>
  );
}

function AlertsStrip({ span, title, note, sub, items }) {
  const reduced = useReducedMotion();
  return (
    <Card span={span} title={title} note={note} sub={sub} badge={items.length}>
      <div className="alerts-strip">
        {items.map((a, i) => (
          <div
            key={i}
            className={`alert-chip ${a.tone}${reduced ? "" : " alert-anim"}`}
            style={reduced ? undefined : { animationDelay: `${i * 55}ms` }}
          >
            <div className="alert-ic">{a.icon || "⚠️"}</div>
            <div>
              <div className="alert-title">{a.title}</div>
              <div className="alert-desc">{a.desc}</div>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function metaContent(m) {
  if (m && typeof m === "object") return <span className={`tag ${m.tone}`}>{m.text}</span>;
  return m;
}

function ListWidget({ span, title, note, sub, items }) {
  return (
    <Card span={span} title={title} note={note} sub={sub}>
      <div>
        {items.map((it, i) => (
          <div className="list-item" key={i}>
            <div className="li-icon">{it.icon || it.title[0]}</div>
            <div className="li-body">
              <div className="li-title">{it.title}</div>
              <div className="li-sub">{it.sub || ""}</div>
            </div>
            <div className="li-meta">{it.meta ? metaContent(it.meta) : ""}</div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function cellContent(cell) {
  if (cell && typeof cell === "object") return <span className={`tag ${cell.tone}`}>{cell.text}</span>;
  return cell;
}

function TableWidget({ span, title, note, sub, cols, rows }) {
  const minW = Math.max(260, cols.length * 92);
  return (
    <Card span={span} title={title} note={note} sub={sub}>
      <div className="table-scroll">
        <table style={{ minWidth: minW }}>
          <thead>
            <tr>
              {cols.map((c, i) => (
                <th key={i}>{c}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                {r.map((cell, ci) => (
                  <td key={ci}>{cellContent(cell)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function ChipsWidget({ span, title, note, sub, chips }) {
  return (
    <Card span={span} title={title} note={note} sub={sub}>
      <div className="chip-row">
        {chips.map((c, i) => (
          <span className="chip" key={i}>
            {c}
          </span>
        ))}
      </div>
    </Card>
  );
}

function InnerChart({ widget }) {
  if (widget.type === "bar") return <BarChart labels={widget.labels} values={widget.values} color={widget.color} max={widget.max} fmt={widget.fmt} />;
  if (widget.type === "line") return <LineChart labels={widget.labels} values={widget.values} color={widget.color} max={widget.max} min={widget.min} />;
  return null;
}

function TabsWidget({ span, title, note, sub, tabs }) {
  const [active, setActive] = useState(0);
  return (
    <Card span={span} title={title} note={note} sub={sub}>
      <div className="tabs">
        {tabs.map((t, i) => (
          <button key={i} className={`tab-btn ${i === active ? "active" : ""}`} onClick={() => setActive(i)}>
            {t.label}
          </button>
        ))}
      </div>
      <InnerChart widget={tabs[active].widget} />
    </Card>
  );
}

function DonutWidget({ span, title, note, sub, percent, color, legend, caption }) {
  return (
    <Card span={span} title={title} note={note} sub={sub}>
      <div style={{ display: "flex", alignItems: "center", gap: 18, flexWrap: "wrap" }}>
        <Ring percent={percent} color={color} size={100} stroke={13} />
        {legend ? (
          <div style={{ flex: 1, minWidth: 150 }}>
            {legend.map((l, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, fontSize: 11.5, padding: "4px 0" }}>
                <span style={{ display: "flex", alignItems: "center", gap: 7 }}>
                  <i style={{ width: 9, height: 9, borderRadius: 2, background: l.color, display: "inline-block" }} />
                  {l.name}
                </span>
                <span style={{ color: "var(--muted)", whiteSpace: "nowrap" }}>{l.value}</span>
              </div>
            ))}
          </div>
        ) : (
          <div>
            <div style={{ fontSize: 22, fontWeight: 800 }}>{percent}%</div>
            <div style={{ fontSize: 11, color: "var(--muted)" }}>{caption || ""}</div>
          </div>
        )}
      </div>
    </Card>
  );
}

function RingClusterWidget({ span, title, note, sub, items }) {
  return (
    <Card span={span} title={title} note={note} sub={sub}>
      <RingCluster items={items} />
    </Card>
  );
}

function Sidebar({ roles, selectedId, onSelect, navOpen, onCloseNav, titleText = "Dashboard Blueprint", helperText = "Static frontend only; no backend or live data is wired up." }) {
  const groups = [];
  roles.forEach((r) => {
    if (!groups.includes(r.group)) groups.push(r.group);
  });

  return (
    <>
      <div className="sidebar-scrim" style={{ display: navOpen ? "block" : "none" }} onClick={onCloseNav} />
      <aside className="sidebar-blueprint" style={{ transform: navOpen ? "translateX(0)" : undefined, boxShadow: navOpen ? "0 0 40px rgba(0,0,0,.4)" : undefined }}>
        <div className="sidebar-brand">
          <div className="brand-mark">A</div>
          <div className="brand-text">
            <b>ACADIX</b>
            <span>by Swostitech Solutions</span>
          </div>
        </div>
        <div className="sidebar-scope">
          <b>{titleText}</b> — {helperText}
        </div>
        <nav>
          {groups.map((g) => (
            <div className="nav-group" key={g}>
              <div className="nav-group-label">{g}</div>
              {roles
                .filter((r) => r.group === g)
                .map((r) => (
                  <button
                    key={r.id}
                    className={`nav-item ${r.id === selectedId ? "active" : ""}`}
                    onClick={() => {
                      onSelect(r.id);
                      onCloseNav();
                    }}
                  >
                    <span className="nav-dot" />
                    <span className="role-name">{r.name}</span>
                  </button>
                ))}
            </div>
          ))}
        </nav>
        <div className="sidebar-foot">
          Reference build for the ACADIX dev team.
          <br />
          Swap widget data for live API/module calls per role.
        </div>
      </aside>
    </>
  );
}

function initials(name) {
  return name
    .split(/[\s/]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();
}

function Topbar({ role, devNotes, onToggleDevNotes, onOpenNav, pillText = "Frontend blueprint · sample data", showMenuButton = true }) {
  return (
    <header className="topbar">
      {showMenuButton && (
        <button className="menu-btn" onClick={onOpenNav} aria-label="Open navigation">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        </button>
      )}
      <div className="topbar-titles">
        <h1>{role.name}</h1>
        <p>{role.sub}</p>
      </div>
      <div className="topbar-right">
        <label className="dn-toggle">
          <input type="checkbox" checked={devNotes} onChange={(e) => onToggleDevNotes(e.target.checked)} />
          <span className="switch" />
          <span className="dn-label">Dev notes</span>
        </label>
        <span className="blueprint-pill">{pillText}</span>
        <div className="avatar">{initials(role.name)}</div>
      </div>
    </header>
  );
}

function Widget({ w, focusMap, setFocusMap }) {
  switch (w.type) {
    case "bar":
      return (
        <Card span={w.span} title={w.title} note={w.note} sub={w.sub}>
          <BarChart labels={w.labels} values={w.values} color={w.color} max={w.max} fmt={w.fmt} />
        </Card>
      );
    case "line":
      return (
        <Card span={w.span} title={w.title} note={w.note} sub={w.sub}>
          <LineChart labels={w.labels} values={w.values} color={w.color} max={w.max} min={w.min} />
        </Card>
      );
    case "combo":
      return (
        <Card span={w.span} title={w.title} note={w.note} sub={w.sub}>
          <ComboChart labels={w.labels} bars={w.bars} line={w.line} barColor={w.barColor} lineColor={w.lineColor} max={w.max} />
        </Card>
      );
    case "multiline":
      return (
        <Card span={w.span} title={w.title} note={w.note} sub={w.sub}>
          <MultiLineChart series={w.series} xLabels={w.xLabels} max={w.max} min={w.min} activeSeries={w.id ? focusMap[w.id] : null} />
        </Card>
      );
    case "ringcluster":
      return <RingClusterWidget span={w.span} title={w.title} note={w.note} sub={w.sub} items={w.items} />;
    case "donut":
      return <DonutWidget span={w.span} title={w.title} note={w.note} sub={w.sub} percent={w.percent} color={w.color} legend={w.legend} caption={w.caption} />;
    case "list":
      return <ListWidget span={w.span} title={w.title} note={w.note} sub={w.sub} items={w.items} />;
    case "table":
      return <TableWidget span={w.span} title={w.title} note={w.note} sub={w.sub} cols={w.cols} rows={w.rows} />;
    case "leaderboard":
      return (
        <Leaderboard
          span={w.span}
          title={w.title}
          note={w.note}
          sub={w.sub}
          badge={w.badge}
          cols={w.cols}
          rows={w.rows}
          sortNote={w.sortNote}
          focusChart={w.focusChart}
          activeRowId={w.focusChart ? focusMap[w.focusChart] : null}
          onFocusRow={(id) => setFocusMap((prev) => ({ ...prev, [w.focusChart]: prev[w.focusChart] === id ? null : id }))}
        />
      );
    case "alerts":
      return <AlertsStrip span={w.span} title={w.title} note={w.note} sub={w.sub} items={w.items} />;
    case "chips":
      return <ChipsWidget span={w.span} title={w.title} note={w.note} sub={w.sub} chips={w.chips} />;
    case "tabs":
      return <TabsWidget span={w.span} title={w.title} note={w.note} sub={w.sub} tabs={w.tabs} />;
    default:
      return null;
  }
}

function Dashboard({ role }) {
  const [focusMap, setFocusMap] = useState({});

  return (
    <div className="dash-enter" key={role.id}>
      <div className="dash-intro">
        <h2>{role.headline}</h2>
        <p>{role.desc}</p>
      </div>
      <div className="grid">
        <KpiRow items={role.kpis} />
        {role.widgets.map((w, i) => (
          <Widget key={i} w={w} focusMap={focusMap} setFocusMap={setFocusMap} />
        ))}
        <div className="source-note">
          <span>{role.sources || ""}</span>
          <span>ACADIX · Role dashboard blueprint v2.0 (React)</span>
        </div>
      </div>
    </div>
  );
}

const COLORS = {
  blue: "#1976D2",
  blueDark: "#125AA0",
  purple: "#7E57C2",
  green: "#1FAE4B",
  orange: "#FB8C00",
  teal: "#26A69A",
  red: "#E53935",
  ink: "#2B3A4A",
  border: "#E2E6EC",
};

const ROLES = [
  { id: "trustee", group: "Leadership", name: "Trustee / Chairman / Director", sub: "Group-level view across all campuses", headline: "Group performance, at a glance", desc: "Cross-campus visibility for governing leadership — enrolment, collections, attendance and academic outcomes rolled up across every institution in the group.", sources: "Attendance from morning roll call · Finance from fee ledger · Academics from board results", kpis: [
    { label: "Students (all campuses)", raw: 6460, sub: "+2.5% vs last term", trend: "up", spark: [6120, 6180, 6250, 6300, 6390, 6460] },
    { label: "Fee collection rate", raw: 89.2, suffix: "%", decimals: 1, sub: "₹4.79 Cr outstanding", trend: "flat", spark: [86, 88, 85, 89, 87, 89.2], sparkColor: COLORS.orange },
    { label: "Overall attendance", raw: 92.4, suffix: "%", decimals: 1, sub: "5,968 / 6,460 today", trend: "up", spark: [90, 91, 89, 92, 93, 92.4], sparkColor: COLORS.purple },
    { label: "Active campuses", raw: 5, sub: "Bengaluru cluster", trend: "flat", spark: [5, 5, 5, 5, 5, 5], sparkColor: COLORS.teal },
  ], widgets: [
    { type: "combo", span: "s7", title: "Collection vs billed target", note: "Source: Fee module, monthly", sub: "₹ lakh · Aug in progress", labels: ["Apr", "May", "Jun", "Jul", "Aug"], bars: [91, 64, 73, 79, 67], line: [85, 85, 86, 87, 88] },
    { type: "ringcluster", span: "s5", title: "Who's on campus", note: "Source: Attendance module", items: [
      { label: "Students", tag: "present", percent: 92, color: COLORS.blue, sub: "5,968/6,460" },
      { label: "Teachers", tag: "present", percent: 94, color: COLORS.green, sub: "330/353" },
      { label: "Support staff", tag: "present", percent: 94, color: COLORS.orange, sub: "202/216" },
    ] },
    { type: "multiline", span: "s7", id: "campusChart", title: "Pass rate — this year vs last", note: "Source: Exam Results, per campus", sub: "Solid = improved YoY · dashed = slipped", xLabels: ["AY 2024-25", "AY 2025-26"], series: [
      { id: "ind", name: "Indiranagar", values: [91.2, 94.6], color: COLORS.blue, dashed: false },
      { id: "ecy", name: "Electronic City", values: [89.9, 93.2], color: COLORS.purple, dashed: false },
      { id: "wtf", name: "Whitefield", values: [88.4, 90.1], color: COLORS.teal, dashed: false },
      { id: "heb", name: "Hebbal", values: [85.1, 89.3], color: COLORS.red, dashed: false },
      { id: "jay", name: "Jayanagar", values: [95.9, 95.2], color: COLORS.orange, dashed: true },
    ] },
    { type: "donut", span: "s5", title: "Revenue mix by fee head", note: "Source: Fee module", percent: 69, color: COLORS.blue, legend: [
      { name: "Tuition", color: COLORS.blue, value: "₹29.70 Cr · 69.4%" },
      { name: "Transport", color: COLORS.teal, value: "₹6.18 Cr · 14.4%" },
      { name: "Hostel", color: COLORS.purple, value: "₹3.96 Cr · 9.3%" },
      { name: "Activities", color: COLORS.orange, value: "₹2.95 Cr · 6.9%" },
    ] },
    { type: "leaderboard", span: "s12", title: "Campus leaderboard", note: "Source: composite of Fee + Attendance + Exam Results", sortNote: "Ranked by pass-rate gain", focusChart: "campusChart", cols: ["Campus", "Students", "Attend.", "Revenue", "Fees", "Pass Δ", "Seats"], rows: [
      { id: "heb", cells: ["Hebbal", "1,120", { text: "89.6%", cls: "cell-warn" }, "₹7.05 Cr", { text: "77.8%", cls: "cell-danger" }, { text: "+4.2", cls: "cell-good" }, "71.3%"] },
      { id: "ind", cells: ["Indiranagar", "1,240", "92.9%", "₹8.42 Cr", "88.3%", { text: "+3.4", cls: "cell-good" }, "82.3%"] },
      { id: "ecy", cells: ["Electronic City", "1,435", "94.1%", "₹9.34 Cr", "91.5%", { text: "+3.3", cls: "cell-good" }, "86.3%"] },
      { id: "wtf", cells: ["Whitefield", "1,685", { text: "90.3%", cls: "cell-warn" }, "₹11.86 Cr", "91.9%", { text: "+1.7", cls: "cell-good" }, "89.7%"] },
      { id: "jay", cells: ["Jayanagar", "980", "96%", "₹6.12 Cr", "95.4%", { text: "-0.7", cls: "cell-danger" }, "94%"] },
    ] },
    { type: "alerts", span: "s12", title: "Management alerts", note: "Cross-module exception feed", items: [
      { tone: "amber", icon: "⚠️", title: "Whitefield", desc: "Student attendance dipped to 90.3% today" },
      { tone: "amber", icon: "📉", title: "Jayanagar", desc: "Pass rate slipped 0.7 pts year-on-year" },
      { tone: "red", icon: "💳", title: "Hebbal", desc: "Fee collection at 77.8% — ₹1.64 Cr outstanding" },
      { tone: "amber", icon: "👤", title: "Hebbal", desc: "Teacher attendance 90.2% today (6 absent)" },
      { tone: "red", icon: "🎯", title: "Hebbal", desc: "Admissions at 71.3% of seat target — intake window closing" },
      { tone: "amber", icon: "⚠️", title: "Hebbal", desc: "Student attendance dipped to 89.6% today" },
    ] },
  ] },
  { id: "principal", group: "Leadership", name: "Principal / Dean", sub: "Single-campus academic + administrative head", headline: "Today at your campus", desc: "A daily operating view for the campus head — staffing, attendance, fee status and anything that needs sign-off today.", sources: "Attendance from morning roll call · Fee from Fee module · Staff from Staff module", kpis: [
    { label: "Students on roll", raw: 1240, sub: "steady", trend: "flat", spark: [1235, 1238, 1236, 1240, 1239, 1240] },
    { label: "Staff present today", raw: 96, suffix: "%", sub: "8 on leave", trend: "flat", spark: [94, 95, 93, 96, 95, 96], sparkColor: COLORS.green },
    { label: "Fee collected (month)", raw: 18.4, prefix: "₹", suffix: "L", decimals: 1, sub: "of ₹24.2L billed", trend: "up", spark: [10, 12, 14, 16, 17, 18.4], sparkColor: COLORS.orange },
    { label: "Pending grievances", raw: 3, sub: "2 opened this week", trend: "down", spark: [6, 5, 5, 4, 4, 3], sparkColor: COLORS.red },
  ], widgets: [
    { type: "ringcluster", span: "s5", title: "Attendance today", note: "Source: Attendance module", items: [
      { label: "Students", tag: "present", percent: 93, color: COLORS.blue, sub: "1,154/1,240" },
      { label: "Teachers", tag: "present", percent: 96, color: COLORS.green, sub: "48/50" },
    ] },
    { type: "line", span: "s7", title: "Student attendance, last 7 days", note: "Source: Attendance module", labels: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Mon"], values: [91, 93, 90, 94, 92, 89, 93], max: 100, color: COLORS.blue },
    { type: "leaderboard", span: "s6", title: "Section-wise attendance", note: "Source: Attendance module", sortNote: "Ranked by attendance today", cols: ["Section", "Students", "Attendance"], rows: [
      { cells: ["10th A", "42", "96%"] }, { cells: ["9th B", "39", { text: "88%", cls: "cell-warn" }] }, { cells: ["11th A", "36", "94%"] },
    ] },
    { type: "table", span: "s6", title: "Upcoming examinations", note: "Source: Exam Results module", cols: ["Class", "Subject", "Date"], rows: [
      ["10th A", "Mathematics", "18 Sep"], ["9th B", "Science", "19 Sep"], ["8th A", "English", "20 Sep"],
    ] },
    { type: "alerts", span: "s12", title: "Needs your attention", note: "Cross-module: Grievance, Visitors, Fee, Staff", items: [
      { tone: "amber", icon: "📝", title: "3 open grievances", desc: "Student Grievance module — 2 opened this week" },
      { tone: "blue", icon: "🧾", title: "Vendor visit logged", desc: "Visitor Management — routine maintenance" },
      { tone: "red", icon: "💰", title: "12 fee reminders due", desc: "Fee module — reminders not yet sent" },
      { tone: "amber", icon: "🧑‍🏫", title: "8 staff on leave", desc: "Staff module — casual + sick + OD" },
    ] },
  ] },
  { id: "superadmin", group: "Administration", name: "Super Admin / IT Admin", sub: "System configuration, roles & permissions", headline: "System configuration", desc: "Platform-wide control: who has access to what, which modules are active, and recent administrative activity.", sources: "Role & Permission module · System audit trail", kpis: [
    { label: "Active users", raw: 214, sub: "+6 this month", trend: "up", spark: [198, 202, 205, 208, 210, 214] },
    { label: "Roles configured", raw: 9, sub: "e.g. Accountant, Librarian", trend: "flat", spark: [7, 7, 8, 8, 9, 9], sparkColor: COLORS.purple },
    { label: "Modules enabled", value: "18 / 18", sub: "all modules live", trend: "flat" },
    { label: "Pending approvals", raw: 5, sub: "new user requests", trend: "down", spark: [9, 8, 7, 6, 6, 5], sparkColor: COLORS.red },
  ], widgets: [
    { type: "leaderboard", span: "s8", title: "Role → module access", note: "Source: Role & Permission module", sortNote: "Ordered by breadth of access", cols: ["Role", "User", "Modules access"], rows: [
      { cells: ["Super Admin", "ALOK", "All modules"] },
      { cells: ["Accountant", "HARIHAR", "Role, Hostel, Transport, Expense, Fee"] },
      { cells: ["Librarian", "SUSHREESANGITA", "Role, Library (12)"] },
      { cells: ["Examiner", "MANOJ", "Role, Exam results"] },
    ] },
    { type: "list", span: "s4", title: "Recent admin activity", note: "System audit trail (illustrative)", items: [
      { icon: "🆕", title: "New role created", sub: '"Front Office"', meta: "2h ago" },
      { icon: "🔑", title: "Permission updated", sub: "Librarian → Book Reports", meta: "5h ago" },
      { icon: "🚫", title: "User deactivated", sub: "Temp staff account", meta: "1d ago" },
    ] },
    { type: "chips", span: "s12", title: "Modules available for role assignment", note: "Matches ACADIX's current module set", chips: ["Dashboard", "Student", "Staff", "Fee", "Library", "Exam Results", "Transport", "Expense", "Other Income", "Hostel", "Timetable", "Lesson Plans", "Mentor", "Academics", "Grievance", "Visitors", "MOU", "Inventory", "AI Assistant"] },
  ] },
  { id: "Register", group: "Administration", name: "Register / Admission Officer", sub: "Enquiries, applications & enrolment", headline: "Admissions this session", desc: "Track every enquiry from first contact through to enrolment, and manage the student record once admitted.", sources: "Admission module · Student Registration module", kpis: [
    { label: "New enquiries (month)", raw: 312, sub: "+18% vs last month", trend: "up", spark: [240, 260, 270, 290, 300, 312] },
    { label: "Applications", raw: 186, sub: "60% of enquiries", trend: "up", spark: [120, 140, 150, 165, 175, 186], sparkColor: COLORS.purple },
    { label: "Admitted", raw: 121, sub: "65% of applications", trend: "flat", spark: [80, 90, 100, 108, 115, 121], sparkColor: COLORS.green },
    { label: "Seats filled", raw: 84.8, suffix: "%", decimals: 1, sub: "of 1,320 target", trend: "up", spark: [70, 74, 78, 80, 82, 84.8], sparkColor: COLORS.orange },
  ], widgets: [
    { type: "bar", span: "s7", title: "Enquiry → Admission funnel", note: "Source: Admission module", labels: ["Enquiries", "Applications", "Admitted"], values: [312, 186, 121], color: COLORS.blue },
    { type: "leaderboard", span: "s5", title: "Recent student search", note: "Source: Student Search screen", sortNote: "Most recently registered first", cols: ["Name", "Class", "Status"], rows: [
      { cells: ["Adil Ahmad", "10th A", { tone: "tag", cls: "green", text: "Active" }] },
      { cells: ["Asif Ahmad", "10th A", { tone: "tag", cls: "green", text: "Active" }] },
      { cells: ["Amarjit Pati", "1st A", { tone: "tag", cls: "blue", text: "New" }] },
    ] },
    { type: "alerts", span: "s12", title: "Pending document verification", note: "Source: Registration module", items: [
      { tone: "amber", icon: "📄", title: "Birth certificate pending", desc: "3 students awaiting document upload" },
      { tone: "amber", icon: "📄", title: "Transfer certificate pending", desc: "2 students awaiting document upload" },
    ] },
  ] },
  { id: "accountant", group: "Administration", name: "Accountant / Finance Officer", sub: "Fee collection, expenses & day book", headline: "Financial control center", desc: "Everything the finance office needs in one place: collection status, dues, expenses and the running day book — with TallyPrime integration (in progress) planned for onward accounting sync.", sources: "Fee module · Expense → Day Book · TallyPrime (integration in progress)", kpis: [
    { label: "Total collected (session)", value: "₹3,900", sub: "of ₹2,23,600 due", trend: "down" },
    { label: "Total dues", value: "₹2,19,700", sub: "outstanding", trend: "down" },
    { label: "Today's collection", raw: 42300, prefix: "₹", sub: "18 receipts", trend: "up", spark: [28000, 31000, 35000, 38000, 40000, 42300], sparkColor: COLORS.green },
    { label: "Expense (month)", value: "₹1.1L", sub: "vendor + operational", trend: "flat" },
  ], widgets: [
    { type: "combo", span: "s7", title: "Collection vs billed target", note: "Source: Fee module, monthly", labels: ["Apr", "May", "Jun", "Jul", "Aug"], bars: [62, 44, 51, 58, 49], line: [58, 58, 58, 58, 58] },
    { type: "donut", span: "s5", title: "Revenue mix by fee head", note: "Source: Fee → Fee Structure", percent: 70, color: COLORS.blue, legend: [
      { name: "Tuition", color: COLORS.blue, value: "₹3,500 / mo" },
      { name: "Transport", color: COLORS.teal, value: "₹800 / mo" },
      { name: "Hostel", color: COLORS.purple, value: "₹4,200 / mo" },
    ] },
    { type: "leaderboard", span: "s12", title: "Sections by outstanding fee dues", note: "Source: Fee Ledger", sortNote: "Ranked by dues outstanding, highest first", cols: ["Section", "Students", "Total due", "Collected", "Balance"], rows: [
      { cells: ["10th A", "42", "₹1,47,000", "₹98,000", { text: "₹49,000", cls: "cell-danger" }] },
      { cells: ["9th B", "39", "₹1,36,500", "₹1,02,000", { text: "₹34,500", cls: "cell-warn" }] },
      { cells: ["11th A", "36", "₹1,26,000", "₹1,10,000", "₹16,000"] },
    ] },
    { type: "alerts", span: "s12", title: "Finance alerts", note: "Fee + Expense modules", items: [
      { tone: "red", icon: "💳", title: "₹2,19,700 outstanding", desc: "Across all sections, this session" },
      { tone: "amber", icon: "🔗", title: "TallyPrime sync pending", desc: "Integration in progress — manual export in use" },
      { tone: "blue", icon: "🧾", title: "18 receipts today", desc: "₹42,300 collected so far" },
    ] },
  ] },
  { id: "hr", group: "Administration", name: "HR Officer", sub: "Staff records, attendance & onboarding", headline: "Workforce overview", desc: "Staff headcount, attendance and leave status across teaching and non-teaching roles.", sources: "Staff module · Role & Permission module", kpis: [
    { label: "Total staff", raw: 353, sub: "330 teaching, 23 support", trend: "flat", spark: [350, 351, 352, 352, 353, 353] },
    { label: "Present today", raw: 93.5, suffix: "%", decimals: 1, sub: "330 / 353", trend: "up", spark: [90, 91, 92, 93, 92, 93.5], sparkColor: COLORS.green },
    { label: "On leave", raw: 14, sub: "casual + sick + OD", trend: "flat", spark: [16, 15, 15, 14, 15, 14], sparkColor: COLORS.orange },
    { label: "Pending onboarding", raw: 2, sub: "documents awaited", trend: "down", spark: [5, 4, 3, 3, 2, 2], sparkColor: COLORS.red },
  ], widgets: [
    { type: "ringcluster", span: "s5", title: "Attendance split", note: "Source: Staff module", items: [
      { label: "Teaching", tag: "present", percent: 96, color: COLORS.blue, sub: "317/330" },
      { label: "Support", tag: "present", percent: 87, color: COLORS.orange, sub: "20/23" },
    ] },
    { type: "leaderboard", span: "s7", title: "Attendance by department", note: "Source: Staff module", sortNote: "Ranked by attendance, highest first", cols: ["Department", "Staff", "Attendance"], rows: [
      { cells: ["Administration", "18", "98%"] }, { cells: ["Science", "64", "96%"] }, { cells: ["Commerce", "41", "94%"] },
      { cells: ["Arts", "55", { text: "91%", cls: "cell-warn" }] }, { cells: ["Sports", "12", { text: "89%", cls: "cell-warn" }] },
    ] },
    { type: "alerts", span: "s12", title: "Leave & onboarding", note: "Source: Staff → Registration", items: [
      { tone: "amber", icon: "🗓️", title: "Sunita Patra", desc: "Casual leave · 2 days — pending approval" },
      { tone: "blue", icon: "✅", title: "Arpita Das", desc: "Sick leave · 1 day — approved" },
      { tone: "amber", icon: "📋", title: "2 onboarding pending", desc: "Document verification awaited" },
    ] },
  ] },
  { id: "librarian", group: "Academic Operations", name: "Librarian", sub: "Catalogue, issue/return & circulation", headline: "Library operations", desc: "Circulation activity and catalogue health for the library team.", sources: "Library module — Book Dashboard, Issue/Return Report", kpis: [
    { label: "Total books", raw: 4897, sub: "4,215 titles", trend: "flat", spark: [4820, 4840, 4860, 4875, 4890, 4897] },
    { label: "Books issued", raw: 815, sub: "currently out", trend: "flat", spark: [790, 800, 805, 810, 812, 815], sparkColor: COLORS.purple },
    { label: "Books returned", raw: 620, sub: "this month", trend: "up", spark: [540, 560, 580, 600, 610, 620], sparkColor: COLORS.green },
    { label: "Total members", raw: 854, sub: "students + staff", trend: "flat", spark: [848, 850, 851, 852, 853, 854] },
  ], widgets: [
    { type: "combo", span: "s7", title: "Circulation trend", note: "Source: Library → Issue/Return Report", sub: "Issued (bars) vs returned (line), last 5 months", labels: ["Apr", "May", "Jun", "Jul", "Aug"], bars: [140, 162, 151, 178, 165], line: [120, 145, 150, 160, 158], barColor: COLORS.teal },
    { type: "leaderboard", span: "s5", title: "Most circulated titles", note: "Source: Most Circulated Book Report", sortNote: "Ranked by times issued", cols: ["Title", "Times issued"], rows: [
      { cells: ["Data Structures", "46"] }, { cells: ["Physics Vol. 2", "39"] }, { cells: ["Algebra Essentials", "33"] },
    ] },
    { type: "alerts", span: "s12", title: "Needs attention", note: "Source: Lost/Damaged, Overdue reports", items: [
      { tone: "amber", icon: "⏰", title: "22 books overdue", desc: "Reminders not yet sent" },
      { tone: "red", icon: "📕", title: "3 lost/damaged", desc: "Pending replacement cost recovery" },
    ] },
  ] },
  { id: "transport", group: "Academic Operations", name: "Transport Manager", sub: "Routes, pickup points & transport fee", headline: "Transport operations", desc: "Route occupancy, running status and transport fee tracking.", sources: "Transport module", kpis: [
    { label: "Active routes", raw: 12, sub: "across campus", trend: "flat", spark: [12, 12, 12, 12, 12, 12] },
    { label: "Students enrolled", raw: 640, sub: "52% of students", trend: "up", spark: [590, 605, 615, 625, 635, 640], sparkColor: COLORS.blue },
    { label: "Buses running today", value: "12 / 12", sub: "on schedule", trend: "flat" },
    { label: "Transport fee dues", value: "₹1.2L", sub: "outstanding", trend: "down" },
  ], widgets: [
    { type: "leaderboard", span: "s7", title: "Route-wise occupancy", note: "Source: Transport module", sortNote: "Ranked by occupancy, highest first", cols: ["Route", "Pickup point", "Students", "Occupancy"], rows: [
      { cells: ["R3", "Nandankanan Rd", "45", "94%"] }, { cells: ["R1", "Patia Square", "42", "88%"] },
      { cells: ["R5", "Chandaka Rd", "40", "83%"] }, { cells: ["R2", "Chandrasekharpur", "38", { text: "79%", cls: "cell-warn" }] },
      { cells: ["R4", "Info Valley", "30", { text: "63%", cls: "cell-warn" }] },
    ] },
    { type: "table", span: "s5", title: "Transport fee dues", note: "Source: Student Transport Fee screen", cols: ["Student", "Route", "Balance"], rows: [
      ["Adil Ahmad", "R1", "₹1,200"], ["Asif Ahmad", "R2", "₹0"],
    ] },
    { type: "alerts", span: "s12", title: "Transport alerts", note: "Source: Transport module", items: [
      { tone: "amber", icon: "🚌", title: "R4 below 65% occupancy", desc: "Consider route consolidation" },
      { tone: "blue", icon: "🕒", title: "All buses on schedule", desc: "No delays reported today" },
    ] },
  ] },
  { id: "hostel", group: "Academic Operations", name: "Hostel Warden", sub: "Occupancy, fee & boarder records", headline: "Hostel operations", desc: "Boarder occupancy, fee collection and block-wise status for residential facilities.", sources: "Hostel module", kpis: [
    { label: "Total boarders", raw: 186, sub: "across 3 blocks", trend: "flat", spark: [180, 182, 183, 184, 185, 186] },
    { label: "Occupancy", raw: 91, suffix: "%", sub: "186 / 204 beds", trend: "up", spark: [86, 87, 88, 89, 90, 91], sparkColor: COLORS.purple },
    { label: "Fee dues", value: "₹86,000", sub: "outstanding", trend: "down" },
    { label: "Maintenance requests", raw: 4, sub: "open tickets", trend: "flat", spark: [6, 5, 5, 4, 4, 4], sparkColor: COLORS.orange },
  ], widgets: [
    { type: "ringcluster", span: "s5", title: "Occupancy by block", note: "Source: Hostel module", items: [
      { label: "Block A", tag: "occupied", percent: 94, color: COLORS.blue, sub: "64/68 beds" },
      { label: "Block B", tag: "occupied", percent: 85, color: COLORS.orange, sub: "58/68 beds" },
      { label: "Block C", tag: "occupied", percent: 94, color: COLORS.purple, sub: "64/68 beds" },
    ] },
    { type: "leaderboard", span: "s7", title: "Boarder fee status (sample)", note: "Source: Hostel → Student Hostel Fee", sortNote: "Highest balance first", cols: ["Student", "Block", "Fee due", "Status"], rows: [
      { cells: ["Rahul Pati", "A", "₹4,200", { tone: "tag", cls: "amber", text: "Due" }] },
      { cells: ["Rajani Pati", "B", "₹0", { tone: "tag", cls: "green", text: "Paid" }] },
    ] },
    { type: "alerts", span: "s12", title: "Maintenance & fee alerts", note: "Source: Hostel module", items: [
      { tone: "amber", icon: "🛠️", title: "4 open maintenance tickets", desc: "Block B — plumbing, electrical" },
      { tone: "red", icon: "💳", title: "₹86,000 outstanding", desc: "Across all boarders" },
    ] },
  ] },
  { id: "exam", group: "Academic Operations", name: "Examination Controller", sub: "Scheduling, evaluation & results", headline: "Examination management", desc: "Exam scheduling status and results publication across classes.", sources: "Exam Results module", kpis: [
    { label: "Exams scheduled", raw: 24, sub: "this term", trend: "flat", spark: [20, 21, 22, 23, 23, 24] },
    { label: "Results published", raw: 18, sub: "75% complete", trend: "up", spark: [10, 12, 14, 15, 16, 18], sparkColor: COLORS.green },
    { label: "Pending evaluation", raw: 6, sub: "awaiting entry", trend: "down", spark: [10, 9, 8, 7, 7, 6], sparkColor: COLORS.orange },
    { label: "Average pass rate", raw: 93.2, suffix: "%", decimals: 1, sub: "+1.4 pts vs last term", trend: "up", spark: [90, 91, 91.5, 92, 92.8, 93.2], sparkColor: COLORS.purple },
  ], widgets: [
    { type: "multiline", span: "s7", id: "classChart", title: "Pass rate — this term vs last", note: "Source: Exam Results module", sub: "Solid = improved · dashed = slipped", xLabels: ["Last term", "This term"], series: [
      { id: "c8", name: "8th", values: [89, 91], color: COLORS.blue },
      { id: "c9", name: "9th", values: [92, 93], color: COLORS.teal },
      { id: "c10", name: "10th", values: [95, 96], color: COLORS.purple },
      { id: "c11", name: "11th", values: [91, 90], color: COLORS.red, dashed: true },
    ] },
    { type: "leaderboard", span: "s5", title: "Pass rate by class", note: "Source: Exam Results module", sortNote: "Ranked by pass rate, highest first", focusChart: "classChart", cols: ["Class", "Pass rate"], rows: [
      { id: "c10", cells: ["10th", "96%"] }, { id: "c9", cells: ["9th", "93%"] }, { id: "c8", cells: ["8th", "91%"] }, { id: "c11", cells: ["11th", { text: "90%", cls: "cell-warn" }] },
    ] },
    { type: "table", span: "s12", title: "Result publishing status", note: "Source: Class Exam Result screen", cols: ["Class & Exam", "Status"], rows: [
      ["10th A — Mid Term", { tone: "green", text: "Published" }], ["9th B — Mid Term", { tone: "amber", text: "In review" }], ["8th A — Mid Term", { tone: "red", text: "Not started" }],
    ] },
  ] },
  { id: "teacher", group: "Academic Operations", name: "Teacher / Faculty", sub: "Timetable, lesson plans & mentoring", headline: "Your teaching day", desc: "Today's classes, lesson plan progress and mentee follow-ups in one view.", sources: "Timetable · Lesson Plan · Mentor modules", kpis: [
    { label: "Classes today", raw: 5, sub: "next: 10th A, 11:00", trend: "flat" },
    { label: "Attendance marked", value: "3 / 5", sub: "classes done", trend: "flat" },
    { label: "Assignments pending", raw: 12, sub: "to grade", trend: "down", spark: [18, 16, 15, 14, 13, 12], sparkColor: COLORS.orange },
    { label: "Mentees", raw: 18, sub: "2 follow-ups due", trend: "flat", spark: [18, 18, 18, 18, 18, 18] },
  ], widgets: [
    { type: "list", span: "s6", title: "Today's timetable", note: "Source: Timetable → Teacher Timetable", items: [
      { icon: "🕒", title: "10th A — Mathematics", sub: "Period 2", meta: "9:45 – 10:30" },
      { icon: "🕒", title: "9th B — Mathematics", sub: "Period 4", meta: "11:45 – 12:30" },
      { icon: "🕒", title: "11th A — Mathematics", sub: "Period 6", meta: "2:00 – 2:45" },
    ] },
    { type: "list", span: "s6", title: "Lesson plan progress", note: "Source: Lesson Plan → Teacher Lesson Plan", items: [
      { title: "Trigonometry — Ch.4", sub: "10th A", meta: { tone: "green", text: "On track" } },
      { title: "Algebra — Ch.7", sub: "9th B", meta: { tone: "amber", text: "Behind" } },
    ] },
    { type: "leaderboard", span: "s12", title: "Mentee follow-ups due", note: "Source: Mentor → Follow Ups", sortNote: "Longest since last check-in first", cols: ["Student", "Last check-in", "Status"], rows: [
      { cells: ["Amarjit Pati", "2 weeks ago", { tone: "tag", cls: "amber", text: "Due" }] }, { cells: ["Asif Ahmad", "3 days ago", { tone: "tag", cls: "green", text: "Recent" }] },
    ] },
  ] },
  { id: "student", group: "End Users", name: "Student", sub: "Web + mobile student portal", headline: "Hello, Anwesha", desc: "A single view of attendance, fees, results and updates — mirrors the ACADIX mobile app.", sources: "Attendance · Fee · Exam Results · Circulars modules", kpis: [
    { label: "Attendance", raw: 93, suffix: "%", sub: "this term", trend: "up", spark: [88, 90, 89, 91, 92, 93], sparkColor: COLORS.blue },
    { label: "Fee dues", value: "₹36,576", sub: "next due 30 Sep", trend: "flat" },
    { label: "Upcoming exams", raw: 2, sub: "next: 18 Sep", trend: "flat" },
    { label: "Pending assignments", raw: 1, sub: "due in 3 days", trend: "down" },
  ], widgets: [
    { type: "line", span: "s7", title: "Attendance trend", note: "Source: Attendance module", labels: ["W1", "W2", "W3", "W4", "W5"], values: [88, 91, 90, 94, 93], max: 100, color: COLORS.blue },
    { type: "list", span: "s5", title: "Today's classes", note: "Source: Student → Timetable", items: [{ title: "No classes scheduled today", sub: "Check back tomorrow" }] },
    { type: "alerts", span: "s12", title: "Notifications", note: "Source: Circulars module", items: [
      { tone: "blue", icon: "📢", title: "Circular · 27 Aug", desc: "Mid-term exam schedule released" },
      { tone: "blue", icon: "📚", title: "Circular · 24 Aug", desc: "Library week — extended hours" },
    ] },
  ] },
  { id: "parent", group: "End Users", name: "Parent", sub: "Web + mobile parent portal", headline: "Your child's overview", desc: "Attendance, fees and academic updates for your ward — switch between children if more than one is enrolled.", sources: "Attendance · Fee · Circulars modules, per student", kpis: [
    { label: "Attendance", raw: 93, suffix: "%", sub: "this term", trend: "up", spark: [88, 90, 89, 91, 92, 93], sparkColor: COLORS.blue },
    { label: "Fee dues", value: "₹36,576", sub: "next due 30 Sep", trend: "flat" },
    { label: "Last exam result", value: "A Grade", sub: "Mid-term, Mathematics", trend: "up" },
    { label: "Pending circulars", raw: 2, sub: "unread", trend: "flat" },
  ], widgets: [
    { type: "tabs", id: "childtabs", span: "s7", title: "Attendance — by child", note: "Source: Attendance module, per student", tabs: [
      { label: "Anwesha (Class 1)", widget: { type: "bar", labels: ["W1", "W2", "W3", "W4"], values: [90, 92, 88, 94], color: COLORS.blue, max: 100 } },
      { label: "Aryan (Class 6)", widget: { type: "bar", labels: ["W1", "W2", "W3", "W4"], values: [85, 88, 91, 89], color: COLORS.purple, max: 100 } },
    ] },
    { type: "list", span: "s5", title: "Fee payment history", note: "Source: Fee module", items: [
      { title: "Term 1 tuition fee", sub: "Paid on 12 Jul", meta: { tone: "green", text: "Paid" } },
      { title: "Transport fee — Aug", sub: "Due 30 Sep", meta: { tone: "amber", text: "Due" } },
    ] },
    { type: "alerts", span: "s12", title: "Circulars & notifications", note: "Source: Circulars module", items: [
      { tone: "blue", icon: "📢", title: "Circular · 27 Aug", desc: "Mid-term exam schedule released" },
      { tone: "blue", icon: "🗓️", title: "Circular · 24 Aug", desc: "PTM scheduled for 5 Oct" },
    ] },
  ] },
  { id: "tp", group: "College-Specific", name: "Training & Placement Officer", sub: "Internships, drives & placements", headline: "Placement season overview", desc: "Company visits, drive status and student placement tracking.", sources: "Training & Placement · MOU modules", kpis: [
    { label: "Companies visited", raw: 14, sub: "this season", trend: "up", spark: [8, 9, 10, 11, 13, 14] },
    { label: "Students placed", raw: 92, suffix: "%", sub: "placement rate", trend: "up", spark: [80, 83, 86, 88, 90, 92], sparkColor: COLORS.green },
    { label: "Highest package", value: "₹9.6 LPA", sub: "vs ₹8.2 LPA last year", trend: "up" },
    { label: "Active drives", raw: 3, sub: "in progress", trend: "flat" },
  ], widgets: [
    { type: "leaderboard", span: "s7", title: "Placement % by department", note: "Source: Training & Placement module", sortNote: "Ranked by placement rate", cols: ["Department", "Students", "Placed"], rows: [
      { cells: ["CS", "120", "96%"] }, { cells: ["EE", "95", "91%"] }, { cells: ["Mech", "110", { text: "88%", cls: "cell-warn" }] }, { cells: ["Civil", "80", { text: "84%", cls: "cell-warn" }] },
    ] },
    { type: "table", span: "s5", title: "Recent drives", note: "Source: Student Placements screen", cols: ["Company", "Module", "Duration"], rows: [
      ["Silicon Tech", "Frontend", "5 days"], ["Wipro", "Backend", "3 days"], ["IBM", "UI/UX", "4 days"],
    ] },
    { type: "alerts", span: "s12", title: "Placement & MOU alerts", note: "Source: MOU + Training & Placement", items: [
      { tone: "blue", icon: "🤝", title: "State Skill Mission", desc: "MOU renewal due next month" },
      { tone: "amber", icon: "📅", title: "3 drives in progress", desc: "Silicon Tech, Wipro, IBM" },
    ] },
  ] },
];

function BlueprintDashboard({ selectedRoleIds = null, titleText = "Dashboard Blueprint", helperText = "Static frontend only; no backend or live data is wired up.", pillText = "Frontend blueprint · sample data" }) {
  const visibleRoles = selectedRoleIds && selectedRoleIds.length ? ROLES.filter((role) => selectedRoleIds.includes(role.id)) : ROLES;
  const defaultRoleId = visibleRoles[0]?.id || ROLES[0].id;
  const [selectedId, setSelectedId] = useState(defaultRoleId);
  const [devNotes, setDevNotes] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const showSidebarNav = visibleRoles.length > 1;

  useEffect(() => {
    document.body.classList.toggle("devnotes-on", devNotes);
  }, [devNotes]);

  useEffect(() => {
    if (visibleRoles.length > 0 && !visibleRoles.some((role) => role.id === selectedId)) {
      setSelectedId(defaultRoleId);
    }
  }, [selectedId, visibleRoles, defaultRoleId]);

  const role = visibleRoles.find((r) => r.id === selectedId) || visibleRoles[0] || ROLES[0];

  return (
    <div className="app-blueprint">
      <style>{blueprintStyles}</style>
      <TooltipProvider>
        {showSidebarNav && (
          <Sidebar
            roles={visibleRoles}
            selectedId={selectedId}
            onSelect={setSelectedId}
            navOpen={navOpen}
            onCloseNav={() => setNavOpen(false)}
            titleText={titleText}
            helperText={helperText}
          />
        )}
        <div className="main-blueprint" style={showSidebarNav ? undefined : { marginLeft: 0 }}>
          <Topbar
            role={role}
            devNotes={devNotes}
            onToggleDevNotes={setDevNotes}
            onOpenNav={() => setNavOpen(true)}
            pillText={pillText}
            showMenuButton={showSidebarNav}
          />
          <div className="dash-wrap">
            <Dashboard role={role} />
          </div>
        </div>
      </TooltipProvider>
    </div>
  );
}

export default BlueprintDashboard;
